import styles from "./ListItem.module.css";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { postCartItem, addFreeToCart, addLocalItem } from "../../store/slices/cartSlice";
import { useState } from "react";
import { downloadProductAssetsArchive } from "../../store/slices/singleProductSlice";
import { formatFocalLengthRange, getLensDisplayName, getLensSeriesLabel } from "../../utils/lens";
import pfp from "../../assets/images/iconplaceholder.png";

const ListItem = ({ asset, animateIn = false, index = 0, returnTo, onProductClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.user);
  const { cart, localItems } = useSelector((state) => state.cart);
  const [isDownloading, setIsDownloading] = useState(false);

  const isInCart = Array.isArray(cart?.items)
    ? cart.items.some((item) => item.product === asset?.id)
    : false;
  const isInLocal = localItems?.some((item) => item.product === asset?.id) || false;
  const isInAnyCart = isInCart || isInLocal;

  const isPurchased = asset?.is_purchased !== undefined
    ? asset.is_purchased
    : (asset?.is_test_force_purchased || false);

  const hasNonGridAssets = Array.isArray(asset?.assets) && asset.assets.some(a => a.asset_type?.is_grid === false);
  const priceTier = (hasNonGridAssets && asset?.full_price != null) ? 'full' : 'grids_only';
  const displayPrice = priceTier === 'full' ? asset?.full_price : asset?.grids_price;
  const displayDiscountedPrice = priceTier === 'full' ? asset?.full_discounted_price : asset?.grids_discounted_price;

  const isFree = asset?.access_type === 'free';
  const href = `/library/product/${asset.slug}`;
  const linkState = returnTo ? { returnTo } : undefined;

  const focalLength = asset.lens?.focal_length_label
    || formatFocalLengthRange(asset.lens?.focal_length_min_mm, asset.lens?.focal_length_max_mm);

  const formatCount = Array.isArray(asset.assets)
    ? new Set(asset.assets.flatMap(a => (a.formats || []).map(f => f.id))).size
    : 0;
  const fileCount = Array.isArray(asset.assets) ? asset.assets.length : 0;

  const resolutionLabel = (() => {
    const nameMatch = asset.camera?.model_name?.match(/(\d+\.?\d*\s*K)/i);
    if (nameMatch) return nameMatch[1].replace(/\s+/, '').toUpperCase();
    const widths = Array.isArray(asset.assets)
      ? asset.assets.flatMap(a => (a.formats || []).map(f => f.res_width).filter(Boolean))
      : [];
    if (!widths.length) return null;
    const toLabel = (w) => {
      if (w >= 12000) return '12K+';
      if (w >= 8000) return '8K';
      if (w >= 6000) return '6K';
      if (w >= 4000) return '4K';
      if (w >= 3000) return '3K';
      if (w >= 2000) return '2K';
      return 'HD';
    };
    const maxLabel = toLabel(Math.max(...widths));
    const minLabel = toLabel(Math.min(...widths));
    return maxLabel === minLabel ? maxLabel : `${minLabel} – ${maxLabel}`;
  })();

  const formatPrice = (price) => {
    if (price == null) return null;
    const num = parseFloat(price);
    if (Number.isNaN(num)) return `$${price}`;
    const hasFraction = Math.abs(num % 1) > 0.0001;
    return hasFraction ? `$${num.toFixed(2)}` : `$${num.toFixed(0)}`;
  };

  const addToCart = () => {
    if (isInAnyCart) return;
    if (isAuthenticated) {
      dispatch(postCartItem({ id: asset.id, priceTier }));
    } else {
      dispatch(addLocalItem({ ...asset, price_tier: priceTier }));
    }
  };

  const handleUserClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (asset.author?.id) navigate(`/profile/${asset.author.id}`);
  };

  const handleFreeAssetClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      dispatch(addLocalItem(asset));
      return;
    }
    try {
      await dispatch(addFreeToCart(asset.id)).unwrap();
    } catch (_) {}
  };

  const paidIcon = (
    <span className={styles.paidIconWrap} data-tooltip="by Matchmove machine">
    <svg className={styles.checkIcon} width="20" height="20" viewBox="0 0 31 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24.9785 9.16719C27.2317 9.16719 29.0582 7.30174 29.0582 5.00059C29.0582 2.69944 27.2317 0.833984 24.9785 0.833984C22.7254 0.833984 20.8989 2.69944 20.8989 5.00059C20.8989 7.30174 22.7254 9.16719 24.9785 9.16719Z" fill="currentColor" />
      <path d="M23.2329 6.22501H23.9192C23.9192 6.59559 24.3893 6.91127 24.9486 6.91127C25.5079 6.91127 25.978 6.59559 25.978 6.22501C25.978 5.84756 25.6211 5.71031 24.8662 5.52845C24.1388 5.34659 23.2329 5.12012 23.2329 4.16621C23.2329 3.55201 23.7373 3.03045 24.4339 2.85545V2.10742H25.4633V2.85545C26.1598 3.03045 26.6642 3.55201 26.6642 4.16621H25.978C25.978 3.79563 25.5079 3.47995 24.9486 3.47995C24.3893 3.47995 23.9192 3.79563 23.9192 4.16621C23.9192 4.54366 24.276 4.68091 25.0309 4.86277C25.7584 5.04463 26.6642 5.2711 26.6642 6.22501C26.6642 6.83921 26.1598 7.36077 25.4633 7.53577V8.2838H24.4339V7.53577C23.7373 7.36077 23.2329 6.83921 23.2329 6.22501Z" fill="white" />
      <g clipPath="url(#clip0_list_check)">
        <path d="M19.2452 10.1953L21.3138 12.2639L13.6865 19.901L9.54932 15.759L11.6179 13.6904L13.6865 15.759L19.2452 10.1953ZM19.2452 11.5678L13.6865 17.1364L11.6179 15.0923L10.9267 15.759L13.6865 18.5138L19.9364 12.2639L19.2452 11.5678Z" fill="currentColor" />
      </g>
      <path d="M24.9801 11.2498V13.2748C24.9801 22.4413 16.8209 26.283 15.189 26.9663C13.5572 26.283 5.39796 22.433 5.39796 13.2748V7.61657C7.11913 7.11121 8.80396 6.48472 10.4404 5.7416C12.0769 5.0191 13.6634 4.18386 15.189 3.24164C16.3782 3.98279 17.6067 4.65604 18.8689 5.25827V5.01661C18.8697 4.50233 18.9327 3.99012 19.0565 3.49163C17.8814 2.91206 16.7399 2.26378 15.6378 1.55L15.189 1.25L14.7484 1.54166C13.164 2.56122 11.5056 3.45548 9.78763 4.21662C8.03852 5.01364 6.22951 5.66574 4.37806 6.16659L3.76611 6.32492V13.2748C3.76611 24.433 14.8056 28.6079 14.9116 28.6496L15.189 28.7496L15.4665 28.6496C15.5807 28.6496 26.612 24.4413 26.612 13.2748V11.0165C26.0808 11.1692 25.5319 11.2477 24.9801 11.2498Z" fill="currentColor" />
      <defs>
        <clipPath id="clip0_list_check">
          <rect width="11.7645" height="11.7645" fill="white" transform="translate(9.54932 9.11719)" />
        </clipPath>
      </defs>
    </svg>
    </span>
  );

  const verifiedBadge = (
    <span className={styles.verifiedBadge} data-tooltip="by Matchmove machine">
      ✓
    </span>
  );

  const downloadIcon = (
    <svg className={styles.downloadIconInline} width="20" height="20" viewBox="0 0 27 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id="mask0_list_dl" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="2" y="7" width="22" height="22">
        <path d="M13 23.621L7.44029 18.0597L8.55129 16.9299L12.2143 20.5929L12.2143 7H13.7857L13.7857 20.5929L17.4471 16.9314L18.5597 18.0597L13 23.621ZM4.53786 29C3.815 29 3.21157 28.758 2.72757 28.274C2.24252 27.789 2 27.185 2 26.4621L2 22.6546H3.57143V26.4621C3.57143 26.7031 3.672 26.9247 3.87314 27.1269C4.07533 27.328 4.2969 27.4286 4.53786 27.4286L21.4621 27.4286C21.7031 27.4286 21.9247 27.328 22.1269 27.1269C22.328 26.9247 22.4286 26.9247 22.4286 26.4621V22.6546H24V26.4621C24 27.185 23.758 27.7884 23.274 28.2724C22.789 28.7575 22.185 29 21.4621 29L4.53786 29Z" fill="currentColor" />
      </mask>
      <g mask="url(#mask0_list_dl)">
        <rect width="27" height="36" transform="matrix(-1 0 0 1 27 0)" fill="currentColor" />
      </g>
    </svg>
  );

  const cartIcon = (
    <svg className={styles.cartIcon} width="22" height="26" viewBox="0 0 30 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9.3999 30.3332C8.66657 30.3332 8.03901 30.0723 7.51724 29.5505C6.99546 29.0287 6.73412 28.4007 6.73324 27.6665C6.73324 26.9332 6.99457 26.3056 7.51724 25.7838C8.0399 25.2621 8.66746 25.0007 9.3999 24.9998C10.1332 24.9998 10.7612 25.2612 11.2839 25.7838C11.8066 26.3065 12.0675 26.9341 12.0666 27.6665C12.0666 28.3998 11.8057 29.0278 11.2839 29.5505C10.7621 30.0732 10.1341 30.3341 9.3999 30.3332ZM22.7332 30.3332C21.9999 30.3332 21.3723 30.0723 20.8506 29.5505C20.3288 29.0287 20.0675 28.4007 20.0666 27.6665C20.0666 26.9332 20.3279 26.3056 20.8506 25.7838C21.3732 25.2621 22.0008 25.0007 22.7332 24.9998C23.4666 24.9998 24.0946 25.2612 24.6172 25.7838C25.1399 26.3065 25.4008 26.9341 25.3999 27.6665C25.3999 28.3998 25.139 29.0278 24.6172 29.5505C24.0955 30.0732 23.4675 30.3341 22.7332 30.3332ZM8.26657 8.99984L11.4666 15.6665H20.7999L24.4666 8.99984H8.26657ZM6.9999 6.33317H28.9999L22.3666 18.3332H10.8666L9.3999 20.9998H25.3999V23.6665H4.8999L8.86657 16.4665L4.06657 6.33317H1.3999V3.6665H5.73324L6.9999 6.33317Z" fill="currentColor" />
    </svg>
  );

  return (
    <Link
      to={href}
      state={linkState}
      className={`${styles.listItem} ${animateIn ? styles.reveal : ''}`}
      style={animateIn ? { animationDelay: `${Math.min(index * 12, 100)}ms` } : undefined}
      onClick={onProductClick}
    >
      <div className={styles.listItemContent}>
        {/* Camera info */}
        <div className={styles.cameraInfo}>
          <span className={styles.cameraBrand}>{asset.camera?.brand || 'Unknown'}</span>
          <span className={styles.cameraModel}>{asset.camera?.model_name || ''}</span>
        </div>

        {/* Lens info */}
        <div className={styles.lensInfo}>
          <span className={styles.lensName}>{getLensSeriesLabel(asset.lens) || getLensDisplayName(asset.lens)}</span>
          {focalLength && (
            <span className={styles.focalLength}>{focalLength}</span>
          )}
        </div>

        {/* Meta — center spacer */}
        <div className={styles.metaCenter}>
          {resolutionLabel && (
            <span className={styles.metaChip}>{resolutionLabel}</span>
          )}
          {resolutionLabel && formatCount > 0 && <span className={styles.metaSep}>·</span>}
          {formatCount > 0 && (
            <span className={styles.metaChip}>{formatCount} {formatCount === 1 ? 'format' : 'formats'}</span>
          )}
          {fileCount > 0 && (
            <>
              {(resolutionLabel || formatCount > 0) && <span className={styles.metaSep}>·</span>}
              <span className={styles.metaChip}>{fileCount} {fileCount === 1 ? 'file' : 'files'}</span>
            </>
          )}
        </div>

        {/* Badge / Contributor */}
        <div className={styles.badge}>
          {isFree
            ? (asset.author
                ? <div className={styles.contributorInfo} onClick={handleUserClick}>
                    <img
                      className={styles.contributorAvatar}
                      src={asset.author.profile_picture || pfp}
                      alt={asset.author.username}
                    />
                    <span className={styles.contributorName}>{asset.author.username}</span>
                  </div>
                : null)
            : paidIcon
          }
        </div>

        {/* Price + Button */}
        <div className={styles.actionArea}>
          {isPurchased ? (
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isDownloading) return;
                setIsDownloading(true);
                try {
                  await dispatch(downloadProductAssetsArchive({ productId: asset.id, slug: asset.slug })).unwrap();
                } catch (_) {}
                setIsDownloading(false);
              }}
              className={`custom-button button-grey ${styles.actionButton} ${!isDownloading ? styles.actionButtonPurchased : ''}`}
              disabled={false}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {downloadIcon}
                <span>{isDownloading ? 'Downloading…' : 'Download'}</span>
              </span>
            </button>
          ) : isInAnyCart ? (
            <button
              className="custom-button button-grey"
              disabled
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                In cart
                {cartIcon}
              </span>
            </button>
          ) : isFree ? (
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFreeAssetClick(e);
              }}
              className="custom-button button-grey"
            >
              Free
            </button>
          ) : displayDiscountedPrice && parseFloat(displayDiscountedPrice) !== parseFloat(displayPrice) ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addToCart();
              }}
              className="custom-button button-blue"
            >
              <span className={styles.priceContent}>
                <span className={styles.originalPrice}>{formatPrice(displayPrice)}</span>
                <span className={styles.discountedPrice}>{formatPrice(displayDiscountedPrice)}</span>
              </span>
              <span className={styles.cartDivider}>{cartIcon}</span>
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addToCart();
              }}
              className="custom-button button-blue"
            >
              <span>{formatPrice(displayPrice)}</span>
              <span className={styles.cartDivider}>{cartIcon}</span>
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ListItem;
